import { stats } from "@app/domain";
import { podiumsSchema, type Variant } from "@app/protocol";
import { useEffect, useState } from "react";
import { API_BASE } from "../config";
import { useStore } from "../store";

/** Podiums move with every counted match, but a frame need not flicker: a minute is fresh. */
const FRESH_MS = 60_000;

const cache = new Map<Variant, { at: number; podiums: stats.Podiums }>();

async function fetchPodiums(session: string, variant: Variant): Promise<stats.Podiums | null> {
  try {
    const response = await fetch(`${API_BASE}/rank-frames?variant=${variant}`, {
      headers: { Authorization: `Bearer ${session}` },
    });
    if (!response.ok) return null;
    const parsed = podiumsSchema.safeParse(await response.json());
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/**
 * Who wears which rank frame in one game (review 0014): a lookup from a player to their
 * frame; bots and strangers to the podium get none. Without an answer nobody is framed.
 */
export function useRankFrames(
  variant: Variant,
): (member: { userId: string; bot?: boolean } | null) => stats.RankFrame | null {
  const session = useStore((s) => s.session);
  const [podiums, setPodiums] = useState(() => cache.get(variant)?.podiums ?? null);
  useEffect(() => {
    const cached = cache.get(variant);
    setPodiums(cached?.podiums ?? null);
    if (session === null || (cached && Date.now() - cached.at < FRESH_MS)) return;
    let live = true;
    void fetchPodiums(session, variant).then((found) => {
      if (found === null) return;
      cache.set(variant, { at: Date.now(), podiums: found });
      if (live) setPodiums(found);
    });
    return () => {
      live = false;
    };
  }, [session, variant]);
  return (member) =>
    member === null || member.bot === true || podiums === null
      ? null
      : stats.rankFrameFor(member.userId, podiums);
}
